import React, { Component } from "react";
import PropTypes from "prop-types";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import CircularProgress from "@material-ui/core/CircularProgress";
import Chip from "@material-ui/core/Chip";
import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

class ConvertToOnDemandPanel extends Component {
  state = {
    orderId: "",
    loadingPreview: false,
    converting: false,
    preview: null,
    error: null,
    dialogOpen: false,
  };

  handleLookup = () => {
    const orderId = String(this.state.orderId || "").trim();
    if (!orderId) {
      this.setState({ error: "יש להזין מספר הזמנה" });
      return;
    }

    this.setState({ loadingPreview: true, error: null, preview: null });
    fetch(`${basePath}/api/v1/on-demand-orders/convert-preview/${encodeURIComponent(orderId)}`, {
      headers: utils.getAdminAuthHeaders(),
    })
      .then((response) => response.json())
      .then((data) => {
        if (!data.ok) throw new Error(data.error || "ההזמנה לא נמצאה");
        this.setState({
          loadingPreview: false,
          preview: data.preview,
          dialogOpen: true,
        });
      })
      .catch((error) => {
        this.setState({ loadingPreview: false, error: error.message });
      });
  };

  handleConfirm = () => {
    const { preview } = this.state;
    if (!preview?.orderId) return;

    this.setState({ converting: true, error: null });
    fetch(`${basePath}/api/v1/on-demand-orders/convert`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...utils.getAdminAuthHeaders(),
      },
      body: JSON.stringify({ orderId: preview.orderId }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (!data.ok) throw new Error(data.error || "ההמרה נכשלה");
        this.setState({
          converting: false,
          dialogOpen: false,
          preview: null,
          orderId: "",
        });
        this.props.onConverted?.(data);
      })
      .catch((error) => {
        this.setState({ converting: false, error: error.message });
      });
  };

  renderPreview() {
    const { preview } = this.state;
    if (!preview) return null;

    return (
      <div>
        {preview.alreadyOnDemand && (
          <Typography style={{ color: "#d97706", fontWeight: 600, marginBottom: 12 }}>
            ההזמנה כבר מופיעה במסך הזמנות מראש
            {preview.onDemandStatus ? ` (סטטוס: ${preview.onDemandStatus})` : ""}.
          </Typography>
        )}
        <Typography variant="body1" style={{ fontWeight: 700, marginBottom: 6 }}>
          הזמנה #{preview.orderId}
        </Typography>
        <Typography variant="body2" style={{ marginBottom: 4 }}>
          לקוח: {preview.customer?.name || "-"}
        </Typography>
        {preview.customer?.phone && (
          <Typography variant="body2" style={{ marginBottom: 4 }}>
            טל: {preview.customer.phone}
          </Typography>
        )}
        {preview.customer?.email && (
          <Typography variant="body2" style={{ marginBottom: 4 }}>
            אימייל: {preview.customer.email}
          </Typography>
        )}
        <Typography variant="body2" style={{ marginBottom: 12, color: "#6b7280" }}>
          סטטוס הזמנה: {preview.status || "-"}
          {preview.createdAt
            ? ` · ${new Date(preview.createdAt).toLocaleDateString("he-IL")}`
            : ""}
        </Typography>
        <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
          תוכן ההזמנה
        </Typography>
        {(preview.items || []).map((item, index) => (
          <div
            key={`${item.ean}-${index}`}
            style={{
              display: "flex",
              gap: 10,
              alignItems: "center",
              padding: "8px 0",
              borderBottom: "1px solid #f3f4f6",
            }}
          >
            {item.imageUrl ? (
              <img
                src={item.imageUrl}
                alt=""
                style={{
                  width: 48,
                  height: 48,
                  objectFit: "contain",
                  border: "1px solid #e5e7eb",
                  borderRadius: 4,
                  background: "#f9fafb",
                }}
              />
            ) : null}
            <div>
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                {[item.brand, item.displayName].filter(Boolean).join(" ")} {item.displaySize || ""}
                {item.quantity ? ` ×${item.quantity}` : ""}
              </Typography>
              <Typography variant="caption" style={{ fontFamily: "monospace", color: "#6b7280" }}>
                EAN: {item.ean}
                {item.price != null ? ` · ₪${item.price}` : ""}
              </Typography>
            </div>
          </div>
        ))}
        {(preview.items || []).length === 0 && (
          <Typography variant="body2" color="error">
            אין פריטים בעגלת ההזמנה
          </Typography>
        )}
      </div>
    );
  }

  render() {
    const { orderId, loadingPreview, converting, dialogOpen, preview, error } = this.state;

    return (
      <Paper
        elevation={0}
        style={{
          padding: 16,
          marginBottom: 16,
          border: "1px solid #bfdbfe",
          background: "#eff6ff",
          borderRadius: 8,
        }}
      >
        <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 8 }}>
          המרת הזמנה רגילה להזמנת מלאי אירופה
        </Typography>
        <Typography variant="body2" style={{ color: "#4b5563", marginBottom: 12 }}>
          הזינו מספר הזמנה ישנה כדי להציג תצוגה מקדימה ולאשר הוספת מאפייני מעקב. כך היא תופיע במסך הזה.
        </Typography>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <TextField
            label="מספר הזמנה"
            value={orderId}
            onChange={(event) => this.setState({ orderId: event.target.value, error: null })}
            onKeyDown={(event) => {
              if (event.key === "Enter") this.handleLookup();
            }}
            style={{ minWidth: 180 }}
          />
          <Button
            variant="contained"
            color="primary"
            disabled={loadingPreview}
            onClick={this.handleLookup}
          >
            {loadingPreview ? <CircularProgress size={18} /> : "הצג תצוגה מקדימה"}
          </Button>
          {error && !dialogOpen && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </div>

        <Dialog
          open={dialogOpen}
          onClose={converting ? undefined : () => this.setState({ dialogOpen: false })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>אישור המרה להזמנת מלאי אירופה</DialogTitle>
          <DialogContent dividers>
            {this.renderPreview()}
            {error && dialogOpen && (
              <Typography variant="body2" color="error" style={{ marginTop: 12 }}>
                {error}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => this.setState({ dialogOpen: false })}
              disabled={converting}
            >
              ביטול
            </Button>
            <Button
              color="primary"
              variant="contained"
              disabled={converting || preview?.alreadyOnDemand || !(preview?.items || []).length}
              onClick={this.handleConfirm}
            >
              {converting ? <CircularProgress size={18} /> : "אשר והוסף למסך"}
            </Button>
            {preview?.alreadyOnDemand && (
              <Chip size="small" label="כבר במסך" style={{ marginRight: 8 }} />
            )}
          </DialogActions>
        </Dialog>
      </Paper>
    );
  }
}

ConvertToOnDemandPanel.propTypes = {
  onConverted: PropTypes.func,
};

export default ConvertToOnDemandPanel;
